import { useTheme } from '@mui/material';
import { vec2, vec3 } from 'gl-matrix';
import React, { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import { PointMm, toPointMm } from '~/domain/geometry/patientGeometry';
import {
    LandmarkPair,
    LandmarkPoint,
    LandmarkResidual,
    provisionalLandmarkResidual,
} from '~/domain/landmark/landmark';
import {
    LandmarkEmphasis,
    landmarkEmphasis,
    showsExpectedMarker,
} from '~/domain/landmark/landmarkEmphasis';
import { landmarkPressAction } from '~/domain/landmark/landmarkPress';
import { paneImageOf } from '~/engine/view/paneImage';
import {
    arePointsOnDisplayedSlice,
    displayedSliceToleranceMm,
    imageToPaneFrame,
    patientToCanvasPoint,
    viewPlaneCoordinateMm,
    viewShowsPlane,
} from '~/engine/view/planeViewGeometry';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { usePlaneRedrawTick } from '~/hooks/viewer/annotationOverlay';
import { useLandmarkController } from '~/hooks/useLandmarkController';
import { useWindowMouseDrag } from '~/hooks/useWindowMouseDrag';
import {
    useSelectedFrameTransform,
    useSelectedRegistrationFrames,
} from '~/hooks/viewer/useSelectedDeformationField';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import {
    draggingLandmarkPairState,
    focusedLandmarkHalfState,
    landmarkPairListState,
} from '~/state/atoms/landmarkAtoms';
import { dicomColorToRgbString } from '~/utils/color';
import { useLandmarkReadout } from '~/hooks/viewer/useLandmarkReadout';
import {
    POINT_MARKER_CROSS_ARM_PX,
    POINT_MARKER_RADIUS_PX,
} from '../pointMarkerGeometry';
import AnnotationOverlaySvg, {
    OFF_PLANE_OPACITY,
} from '../AnnotationOverlaySvg';

const RESIDUAL_OFFSET_PX = 16;

const RECEDED_COLOR = 'rgb(150, 150, 150)';

const EMPHASIS_STYLE: Record<
    LandmarkEmphasis,
    { opacity: number; strokeWidth: number }
> = {
    focus: { opacity: 1, strokeWidth: 2.5 },
    counterpart: { opacity: 1, strokeWidth: 2 },
    resting: { opacity: 1, strokeWidth: 1.5 },
    background: { opacity: 0.3, strokeWidth: 1 },
};

interface LandmarkOverlayProps {
    view: PlaneView | null;
}

interface ProjectedLandmark {
    readonly key: string;
    readonly pairId: string;
    readonly half: 'fixed' | 'moving' | null;
    readonly name: string;
    readonly color?: string;
    readonly x: number;
    readonly y: number;
    readonly onSlice: boolean;
    readonly selected: boolean;
    readonly expected: boolean;
}

export default function LandmarkOverlay({ view }: LandmarkOverlayProps) {
    const intl = useIntl();
    const theme = useTheme();
    const placedColor = theme.palette.secondary.main;
    const expectedColor = theme.palette.primary.main;
    const pairs = useRecoilValue(landmarkPairListState);
    const viewManager = useRecoilValue(viewManagerState);
    const transform = useSelectedFrameTransform();
    const frames = useSelectedRegistrationFrames();
    // Re-renders on pan, zoom and slice changes, which the projection below reads from the view.
    usePlaneRedrawTick(viewManager);
    const { movePoint, togglePair, selectedPairId } = useLandmarkController();
    const [focusedHalf, setFocusedHalf] = useRecoilState(
        focusedLandmarkHalfState
    );
    const [draggingPairId, setDraggingPairId] = useRecoilState(
        draggingLandmarkPairState
    );

    // Coordinates are written back on release; the residual updates during the drag.
    const dragRef = useRef<{
        pairId: string;
        half: 'fixed' | 'moving';
        startClient: vec2;
        startPaneMm: PointMm;
        grabbedMm: PointMm;
    } | null>(null);
    const [drag, setDrag] = useState<{
        key: string;
        dx: number;
        dy: number;
        residual: LandmarkResidual | undefined;
    } | null>(null);

    const draggedPointMm = useCallback(
        (event: MouseEvent): PointMm | null => {
            const current = dragRef.current;
            if (!view || !current) {
                return null;
            }
            const pane = paneImageOf(view);
            if (pane.kind === 'none') {
                return null;
            }
            const to = view.clientToPatientCoordinates(
                vec2.fromValues(event.clientX, event.clientY)
            );
            const delta = vec3.subtract(vec3.create(), to, current.grabbedMm);
            // Dragged on the pane; stored in the frame of the image the pane shows.
            return pane.toImageFrame(
                toPointMm(vec3.add(vec3.create(), current.startPaneMm, delta))
            );
        },
        [view]
    );

    const onDragMove = useCallback(
        (event: MouseEvent) => {
            const current = dragRef.current;
            if (!current) {
                return;
            }
            const pointMm = draggedPointMm(event);
            const pair = pairs.find((p) => p.id === current.pairId);
            setDrag({
                key: `${current.pairId}-${current.half}`,
                dx: event.clientX - current.startClient[0],
                dy: event.clientY - current.startClient[1],
                residual:
                    pair && pointMm && transform
                        ? provisionalLandmarkResidual(
                              pair,
                              current.half,
                              pointMm,
                              transform
                          )
                        : undefined,
            });
        },
        [draggedPointMm, pairs, transform]
    );

    const onDragEnd = useCallback(
        (event: MouseEvent) => {
            const current = dragRef.current;
            const pointMm = draggedPointMm(event);
            dragRef.current = null;
            setDrag(null);
            setDraggingPairId(null);
            if (current && pointMm) {
                movePoint(current.pairId, current.half, pointMm);
            }
        },
        [draggedPointMm, movePoint, setDraggingPairId]
    );

    const startDrag = useWindowMouseDrag(onDragMove, onDragEnd);

    const onMarkerMouseDown = useCallback(
        (event: React.MouseEvent, landmark: ProjectedLandmark) => {
            // Otherwise the landmark tool underneath places another point on top.
            event.stopPropagation();
            event.preventDefault();
            const pair = pairs.find((p) => p.id === landmark.pairId);
            const action = landmarkPressAction(
                pair,
                landmark.half,
                selectedPairId
            );
            if (action.kind === 'select') {
                togglePair(landmark.pairId);
                setFocusedHalf(landmark.half);
                return;
            }
            const startClient = vec2.fromValues(event.clientX, event.clientY);
            const grabbed = view?.clientToPatientCoordinates(startClient);
            const pane = view ? paneImageOf(view) : undefined;
            if (!grabbed || !pane || pane.kind === 'none') {
                return;
            }
            setFocusedHalf(action.half);
            setDraggingPairId(landmark.pairId);
            dragRef.current = {
                pairId: landmark.pairId,
                half: action.half,
                startClient,
                startPaneMm: pane.fromImageFrame(action.point.pointMm),
                grabbedMm: toPointMm(grabbed),
            };
            startDrag(event);
        },
        [
            pairs,
            selectedPairId,
            startDrag,
            togglePair,
            setFocusedHalf,
            setDraggingPairId,
            view,
        ]
    );

    const { nameOf, textOf } = useLandmarkReadout();

    const colorOf = useCallback(
        (pair: LandmarkPair) =>
            pair.color ? dicomColorToRgbString(pair.color) : undefined,
        []
    );

    const projected = ((): ProjectedLandmark[] => {
        if (!view) {
            return [];
        }
        const pane = paneImageOf(view);
        if (pane.kind === 'none') {
            return [];
        }
        const ontoPane = imageToPaneFrame(pane);
        const tolerance = displayedSliceToleranceMm(view);
        // The frame of the image the pane shows, which on a pair's second pane is not the row image's.
        const viewFrame = pane.image.parent.frameOfReferenceUid;
        const out: ProjectedLandmark[] = [];
        pairs.forEach((pair, index) => {
            if (!pair.visible) {
                return;
            }
            for (const half of ['fixed', 'moving'] as const) {
                const stored = pair[half];
                if (!stored || !viewShowsPlane(view, stored.plane)) {
                    continue;
                }
                const point: LandmarkPoint = {
                    ...stored,
                    pointMm: ontoPane.point(stored.pointMm),
                    plane: ontoPane.plane(stored.plane),
                };
                const canvas = patientToCanvasPoint(point.pointMm, view);
                out.push({
                    key: `${pair.id}-${half}`,
                    pairId: pair.id,
                    half,
                    name: nameOf(pair, index),
                    color: colorOf(pair),
                    x: canvas.x,
                    y: canvas.y,
                    onSlice: onDisplayedSlice(view, point, tolerance),
                    selected: pair.id === selectedPairId,
                    expected: false,
                });
            }
            const wantsExpected = showsExpectedMarker(
                !!pair.fixed && !!pair.moving
            );
            if (transform && frames && viewFrame && wantsExpected) {
                const mapped =
                    pair.fixed && viewFrame === frames.moving
                        ? transform.forward(pair.fixed.pointMm)
                        : pair.moving && viewFrame === frames.fixed
                          ? transform.backward(pair.moving.pointMm)
                          : undefined;
                if (mapped) {
                    const expectedMm = ontoPane.point(toPointMm(mapped));
                    const canvas = patientToCanvasPoint(expectedMm, view);
                    out.push({
                        key: `${pair.id}-expected`,
                        pairId: pair.id,
                        half: null,
                        name: nameOf(pair, index),
                        color: colorOf(pair),
                        x: canvas.x,
                        y: canvas.y,
                        // A prediction is a point in space; drawn on-slice it hides the through-plane gap.
                        onSlice:
                            Math.abs(
                                viewPlaneCoordinateMm(expectedMm, view) -
                                    view.planeCoordinates
                            ) <= tolerance,
                        selected: pair.id === selectedPairId,
                        expected: true,
                    });
                }
            }
        });
        return out;
    })();

    if (!view || projected.length === 0) {
        return null;
    }

    return (
        <AnnotationOverlaySvg>
            {projected.map((landmark) => {
                const emphasis = landmarkEmphasis({
                    pairId: landmark.pairId,
                    half: landmark.half,
                    focusedHalf,
                    draggingPairId,
                });
                const style = EMPHASIS_STYLE[emphasis];
                const color =
                    emphasis === 'background'
                        ? RECEDED_COLOR
                        : (landmark.color ??
                          (landmark.expected ? expectedColor : placedColor));
                const dragging = drag?.key === landmark.key ? drag : null;
                const x = landmark.x + (dragging?.dx ?? 0);
                const y = landmark.y + (dragging?.dy ?? 0);
                const draggable = landmark.half !== null && landmark.selected;
                return (
                    <g
                        key={landmark.key}
                        opacity={
                            style.opacity *
                            (landmark.onSlice ? 1 : OFF_PLANE_OPACITY)
                        }
                        onMouseDown={(event) =>
                            onMarkerMouseDown(event, landmark)
                        }
                        style={{
                            pointerEvents: landmark.expected ? 'none' : 'auto',
                            cursor: dragging
                                ? 'grabbing'
                                : draggable
                                  ? 'grab'
                                  : 'pointer',
                        }}
                        data-testid={
                            landmark.expected
                                ? 'landmark-expected-marker'
                                : 'landmark-marker'
                        }
                    >
                        <circle
                            cx={x}
                            cy={y}
                            r={POINT_MARKER_RADIUS_PX}
                            fill="none"
                            stroke={color}
                            strokeWidth={style.strokeWidth}
                            strokeDasharray={landmark.onSlice ? 'none' : '3 3'}
                        />
                        <line
                            x1={x - POINT_MARKER_CROSS_ARM_PX}
                            y1={y}
                            x2={x + POINT_MARKER_CROSS_ARM_PX}
                            y2={y}
                            stroke={color}
                            strokeWidth={style.strokeWidth}
                        />
                        <line
                            x1={x}
                            y1={y - POINT_MARKER_CROSS_ARM_PX}
                            x2={x}
                            y2={y + POINT_MARKER_CROSS_ARM_PX}
                            stroke={color}
                            strokeWidth={style.strokeWidth}
                        />
                        <text
                            x={x + POINT_MARKER_RADIUS_PX + 5}
                            y={y}
                            fill={color}
                            fontSize={13}
                            fontFamily={theme.typography.fontFamily}
                            fontWeight={600}
                            dominantBaseline="central"
                            stroke="rgba(0, 0, 0, 0.75)"
                            strokeWidth={3}
                            paintOrder="stroke"
                        >
                            {landmark.expected
                                ? intl.formatMessage(
                                      { id: 'landmark.marker.expected' },
                                      { name: landmark.name }
                                  )
                                : landmark.name}
                        </text>
                        {dragging?.residual && (
                            <text
                                x={x + POINT_MARKER_RADIUS_PX + 5}
                                y={y + RESIDUAL_OFFSET_PX}
                                fill={color}
                                fontSize={13}
                                fontFamily={theme.typography.fontFamily}
                                fontWeight={600}
                                dominantBaseline="central"
                                stroke="rgba(0, 0, 0, 0.75)"
                                strokeWidth={3}
                                paintOrder="stroke"
                                data-testid="landmark-live-residual"
                            >
                                {dragging.residual.status === 'measured'
                                    ? intl.formatMessage(
                                          { id: 'landmark.marker.residual' },
                                          { v: textOf(dragging.residual) }
                                      )
                                    : intl.formatMessage({
                                          id: `registrationQa.tre.${dragging.residual.status}`,
                                      })}
                            </text>
                        )}
                    </g>
                );
            })}
        </AnnotationOverlaySvg>
    );
}

function onDisplayedSlice(
    view: PlaneView,
    point: LandmarkPoint,
    toleranceMm: number
): boolean {
    return arePointsOnDisplayedSlice(
        [point.pointMm],
        view,
        point.plane,
        toleranceMm
    );
}
